import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormBuilder } from '../../utils/useFormBuilder';
import { useValidator } from '../../utils/useValidator';
import { getOrFetch } from '../../utils/lookupCache';
import { fetchData } from '../../utils/apiHelpers';
import { getAll } from '../../api';
import { getParts } from '../../api/partsAPI';
import AddPartModal from './AddPartModal';

const today = new Date().toISOString().split('T')[0];

const DEFAULT_ROTABLE = {
  serialNumber: '',
  partId: null,
  statusId: 1,
  repairStatusId: 1,
  componentTypeId: 1,
  groups: [],
  estimatedRepairTime: 0,
  numberOfTurns: 0,
  manufactureDate: today,
  arrangementNumber: '',
  warrantyStart: today,
  warrantyEnd: today,
  notes: '',
  usage: 0,
  repairStatus: null,
};

const sanitizeRotableValues = (values) => {
  const nullableFields = [
    'manufactureDate',
    'warrantyStart',
    'warrantyEnd',
  ];
  const numericFields = ['estimatedRepairTime', 'numberOfTurns', 'usage'];

  return nullableFields.reduce((acc, field) => {
    const rawValue = values[field];
    const trimmedValue = typeof rawValue === 'string' ? rawValue.trim() : rawValue;

    if (trimmedValue === '' || trimmedValue === undefined || trimmedValue === null) {
      acc[field] = null;
      return acc;
    }

    if (numericFields.includes(field)) {
      const numericValue = Number(trimmedValue);
      acc[field] = Number.isFinite(numericValue) ? numericValue : null;
      return acc;
    }

    acc[field] = rawValue;
    return acc;
  }, { ...values });
};

const RotableFormModal = ({ show, onClose, onSave, initialValues }) => {
  const [rotable, setRotable] = useState(DEFAULT_ROTABLE);
  const [selectedPartOption, setSelectedPartOption] = useState(null);
  const [partSearchText, setPartSearchText] = useState('');
  const [showAddPartModal, setShowAddPartModal] = useState(false);
  const [addPartInitialValues, setAddPartInitialValues] = useState({});
  const [partSelectRefreshKey, setPartSelectRefreshKey] = useState(0);

  const PARTS_PAGE_SIZE = 25;
  const addNewPartOption = useMemo(
    () => ({ value: 'add-new', label: '+ Add New Part', isAddNew: true }),
    [],
  );

  const formatPartOptionLabel = useCallback((partNumber, description) => {
    const safeNumber = typeof partNumber === 'string' || typeof partNumber === 'number'
      ? String(partNumber).trim()
      : '';
    const safeDescription = typeof description === 'string' ? description.trim() : '';
    return [safeNumber, safeDescription].filter(Boolean).join(' – ');
  }, []);

  const handlePartSearch = useCallback(
    async (inputValue = '', { page } = {}) => {
      const raw = typeof inputValue === 'string' ? inputValue : inputValue?.search ?? '';
      const trimmed = raw.trim();
      setPartSearchText(trimmed);
      const pageNumber = typeof page === 'number' && page > 0 ? page : 1;
      const shouldLoadDefault = trimmed === '';

      if (!shouldLoadDefault && trimmed.length < 2) {
        return { options: [addNewPartOption], hasMore: false };
      }

      try {
        const resp = await getParts({
          page: pageNumber,
          pageSize: PARTS_PAGE_SIZE,
          search: shouldLoadDefault ? '' : trimmed,
        });
        const items = Array.isArray(resp?.data) ? resp.data : Array.isArray(resp) ? resp : [];
        const options = items.map((item) => ({
          value: item.id,
          label: formatPartOptionLabel(item.partNumber, item.description) || `Part ${item.id}`,
          raw: item,
        }));
        const optionsWithAddNew = pageNumber === 1 ? [addNewPartOption, ...options] : options;
        const totalCount =
          typeof resp?.total === 'number'
            ? resp.total
            : typeof resp?.count === 'number'
              ? resp.count
              : typeof resp?.meta?.total === 'number'
                ? resp.meta.total
                : undefined;
        const hasMore =
          typeof resp?.hasMore === 'boolean'
            ? resp.hasMore
            : typeof totalCount === 'number'
              ? pageNumber * PARTS_PAGE_SIZE < totalCount
              : items.length === PARTS_PAGE_SIZE;

        return {
          options: optionsWithAddNew,
          totalCount,
          pageSize: PARTS_PAGE_SIZE,
          hasMore,
        };
      } catch (err) {
        console.error('Error fetching parts:', err);
        return { options: [addNewPartOption], hasMore: false };
      }
    },
    [addNewPartOption, formatPartOptionLabel],
  );

  useEffect(() => {
    setRotable({ ...DEFAULT_ROTABLE, ...(initialValues || {}) });
  }, [initialValues, show]);

  useEffect(() => {
    if (!show) return;
    const part = initialValues?.part;
    if (part?.id) {
      setSelectedPartOption({
        value: part.id,
        label: formatPartOptionLabel(part.partNumber, part.description) || `Part ${part.id}`,
        raw: part,
      });
      return;
    }

    if (initialValues?.partId) {
      setSelectedPartOption((prev) =>
        prev?.value === initialValues.partId ? prev : null,
      );
    } else {
      setSelectedPartOption(null);
    }
  }, [formatPartOptionLabel, initialValues, show]);

  useEffect(() => {
    const ensureStatusDefault = async () => {
      if (rotable.statusId) return;
      try {
        const resp = await getOrFetch('stockItemStatus', () => fetchData(getAll, { apiName: 'stock-item-status' }));
        const options = Array.isArray(resp) ? resp : Array.isArray(resp?.data) ? resp.data : [];
        const inStock = options.find((opt) => opt?.name === 'In Stock');
        if (inStock?.id) {
          setRotable((prev) => ({ ...prev, statusId: prev.statusId || inStock.id }));
        }
      } catch (err) {
        console.error('Unable to set default rotable status', err);
      }
    };

    ensureStatusDefault();
  }, [rotable.statusId]);

  const rotableFields = useMemo(
    () => [
      { field: 'serialNumber', label: 'Serial Number', type: 'text', size: '4', maxLength: 45, required: true },
      {
        field: 'partId',
        label: 'Part Number',
        type: 'paginated-select',
        size: '4',
        loadOptions: handlePartSearch,
        selectedOption: selectedPartOption,
        setSelectedOption: setSelectedPartOption,
        required: true,
        minSearchLength: 2,
        onChange: (option, setFormData, field, setSelectedOptionFn) => {
          if (option?.isAddNew) {
            if (setSelectedOptionFn) {
              setSelectedOptionFn(null);
            }
            setAddPartInitialValues((prev) => ({
              ...prev,
              partNumber: partSearchText,
            }));
            setShowAddPartModal(true);
            return;
          }

          if (setSelectedOptionFn) {
            setSelectedOptionFn(option ?? null);
          }

          setFormData((prev) => ({
            ...prev,
            [field]: option?.value ?? null,
            partId: option?.value ?? null,
            part: option?.raw ?? null,
          }));
        },
      },
      {
        field: 'statusId',
        label: 'Status',
        type: 'select',
        size: '4',
        api: 'stock-item-status',
        displayField: 'name',
        required: true,
      },
      {
        field: 'repairStatusId',
        label: 'Repair Status',
        type: 'select',
        size: '4',
        api: 'repair-status',
        displayField: 'name',
      },
      {
        field: 'componentTypeId',
        label: 'Component Type',
        type: 'select',
        size: '4',
        api: 'component-type',
        displayField: 'name',
      },
      {
        field: 'groups',
        label: 'Applicable Models',
        type: 'multi-select',
        size: '8',
        api: 'groups',
        displayField: 'groupName',
        isMulti: true,
      },
      {
        field: 'manufactureDate',
        label: 'Manufacture Date',
        type: 'date',
        size: '3',
        noFutureDate: true,
      },
      { field: 'arrangementNumber', label: 'Arrangement', type: 'text', size: '3', maxLength: 25 },
      {
        field: 'estimatedRepairTime',
        label: 'Estimated Repair Time (Days)',
        type: 'number',
        size: '3',
        mustBePositiveInteger: true,
        allowZeroValue: true,
      },
      {
        field: 'numberOfTurns',
        label: 'Number of Turns',
        type: 'number',
        size: '3',
        mustBePositiveInteger: true,
        allowZeroValue: true,
      },

      { field: 'warrantyStart', label: 'Warranty Start', type: 'date', size: '3' },
      {
        field: 'warrantyEnd',
        label: 'Warranty End',
        type: 'date',
        size: '3',
        mustBeAfterField: 'warrantyStart',
        mustBeAfterFieldMessage: 'Warranty end date cannot be before the start date',
      },
      { field: 'notes', label: 'Notes', type: 'textarea', size: '12', lines: '3' },
      {
        field: 'usage',
        label: 'Usage',
        type: 'number',
        size: '3',
        mustBePositiveInteger: true,
        allowZeroValue: true,
      },
    ],
    [handlePartSearch, partSearchText, selectedPartOption],
  );

  const { errors, validate } = useValidator(rotableFields, rotable);
  const { renderField } = useFormBuilder(rotableFields, rotable, setRotable, errors, {
    refreshKey: partSelectRefreshKey,
  });

  const handleSave = () => {
    const isValid = validate();

    if (!isValid) {
      return;
    }

    const sanitizedRotable = sanitizeRotableValues(rotable);
    const { partNumber, ...payload } = sanitizedRotable;
    onSave?.(payload);
    onClose?.();
  };

  if (!show) return null;

  return (
    <div className="modal show d-block" tabIndex="-1">
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Add Rotable</h5>
            <button className="btn-close" onClick={onClose} />
          </div>
          <div className="modal-body">
            <div className="row g-3">{rotableFields.map(renderField)}</div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSave}>Save Rotable</button>
          </div>
        </div>
      </div>
      <AddPartModal
        show={showAddPartModal}
        onClose={() => setShowAddPartModal(false)}
        initialValues={addPartInitialValues}
        onPartCreated={(payload) => {
          const created = payload?.part;
          if (!created?.id) return;
          const nextOption = {
            value: created.id,
            label: formatPartOptionLabel(created.partNumber, created.description) || `Part ${created.id}`,
            raw: created,
          };
          setSelectedPartOption(nextOption);
          setRotable((prev) => ({
            ...prev,
            partId: created.id,
            part: created,
          }));
          setShowAddPartModal(false);
          setAddPartInitialValues({});
          setPartSelectRefreshKey((prev) => prev + 1);
        }}
      />
    </div>
  );
};

export default RotableFormModal;
